/**
 * The vocabulary both ends of the wire use to talk about who someone is.
 *
 * Kept in `shared` rather than in the API because the frontend renders decisions
 * from these same values — a role string that means one thing in Express and
 * another in React is how a contractor ends up seeing an admin menu.
 */
export const ROLES = ["SYSTEM_ADMIN", "UNIFIBER_INTERNAL", "EXTERNAL_OPERATOR"];
export const AUTH_TYPES = ["LOCAL", "ENTRA_ID", "BOTH"];
/**
 * What the sign-in endpoint tells the client to do next.
 *
 * The client never decides this for itself: every step but `AUTHENTICATED` comes
 * with a short-lived temp token that is only accepted by the endpoint for that one
 * step. A client that skipped ahead would simply be refused.
 */
export const LOGIN_STEPS = [
    "AUTHENTICATED",
    "TOTP_SETUP",
    "TOTP_VERIFY",
    "PASSWORD_EXPIRED",
];
/** True for the two internal Unifiber roles, which are never row-scoped. */
export function isInternal(role) {
    return role === "SYSTEM_ADMIN" || role === "UNIFIBER_INTERNAL";
}
/** True for roles that may reach the administration screens. */
export function isAdmin(role) {
    return role === "SYSTEM_ADMIN";
}
/**
 * THE contractor-scoping rule (hard rule 9).
 *
 * Returns `null` when the user sees every row, or the exact list of DMP
 * `contractor` values they are limited to. The table, the KPI cards, the charts
 * and the Excel export must all derive their filter from this one call, or they
 * drift apart and Ubi loses the one thing the product exists to provide: the same
 * number for everybody.
 *
 * ⚠ An empty array is a real and meaningful answer — "this contractor is mapped to
 * no DMP code, so no rows match". It must never be read as "no filter". That
 * mistake turns a misconfigured company into one that sees its competitors' work,
 * which is the single worst failure this application could have. `matchesScope`
 * below exists so no call site has to remember it.
 */
export function contractorScope(user) {
    return isInternal(user.role) ? null : user.dmpContractorCodes;
}
/**
 * Whether one row's `contractor` is visible under a scope.
 *
 * `null` contractors — 20 of them in production on 16 Sep 2026 — are visible to
 * internal users only. No contractor may claim a row that DMP has not assigned to
 * them, and silently handing those rows to everybody would be worse than leaving
 * them unactioned, which is already a question outstanding with Ubi.
 */
export function matchesScope(rowContractor, scope) {
    if (scope === null)
        return true;
    if (rowContractor === null || rowContractor === undefined)
        return false;
    return scope.includes(rowContractor);
}
//# sourceMappingURL=auth.js.map